/**
 * Location Availability Utilities
 *
 * Classifies a location by whether it still has bookable pickup stops,
 * driving the map marker icon (grey vs. active) and the sold-out /
 * season-complete messaging (#314).
 */

import type { Location, Stop } from "@/types/location";
import { getStopStatusDisplay } from "@/utils/stop-status";

/**
 * - `active`: at least one upcoming stop can still be attended
 * - `sold-out`: upcoming stops exist, but none are available
 * - `season-complete`: every stop (if any) is in the past
 */
export type LocationAvailability = "active" | "sold-out" | "season-complete";

/**
 * Last calendar day a stop occurs
 *
 * @remarks
 * Recurring stops run until `repeat_until`, so that date (when present)
 * is the stop's true end. Feed dates are MM/DD/YYYY, which `new Date`
 * parses as local time.
 */
function stopFinalDate(stop: Stop): Date | null {
  const raw = stop.repeat_until || stop.date;
  const date = new Date(raw);
  if (isNaN(date.getTime())) return null;

  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * Whether a stop's final day has fully elapsed
 *
 * @remarks
 * A stop happening today counts as upcoming until the day ends.
 * Unparseable dates count as past so bad data can't keep a location
 * looking active.
 */
export function isStopPast(stop: Stop): boolean {
  const finalDate = stopFinalDate(stop);
  if (!finalDate) return true;

  return finalDate < new Date();
}

/**
 * Classify a location's overall availability
 *
 * @remarks
 * Availability of an upcoming stop reuses getStopStatusDisplay so the
 * pin agrees with the rest of the UI: a sold-out stop with walk-ups
 * still counts as attendable and keeps the location active. Locations
 * derive state purely from feed data, so an unavailable location
 * automatically reactivates when a new available stop appears.
 */
export function getLocationAvailability(location: Location): LocationAvailability {
  const upcomingStops = (location.stops ?? []).filter((stop) => !isStopPast(stop));
  if (upcomingStops.length === 0) return "season-complete";

  const hasAvailableStop = upcomingStops.some(
    (stop) => getStopStatusDisplay(stop).isAvailable,
  );
  return hasAvailableStop ? "active" : "sold-out";
}
